import React, { useEffect, useRef, useState } from "react";
import { ASSETS_ROUTES } from "../lib/common/constants";
import { TApiLab } from "../lib/types/labs";
import { TLogoCustomization } from "../lib/types/dentalTechs";
import { FileUploaderComponent, useFileManagement } from "../services/file-management";
import { TProcessStates } from "../lib/types/general";
import { getCacheKillQueryString } from "../lib/common/api";
import { useLabs } from "../services/laboratories";
import { useAuthContext } from "../contexts/auth-context";

type TLogoLabProps = {
    userInfo: TApiLab;
    editable: boolean;
    workingState: TProcessStates;
    isSomePartBeingEdited: boolean;
    setInappropriateContent: (value: boolean) => void;
    setUnsupportedImageType: (value: boolean) => void;
    setUploadErrorMessage?: (value: string) => void;
    extraClass: string;
    onCustomizeModeChange?: (isCustomizing: boolean) => void;
    onboardingStepCompleted?: () => void;
};

export const LogoLab = (props: TLogoLabProps) => {
    const { userInfo, editable, workingState, isSomePartBeingEdited, setInappropriateContent, setUnsupportedImageType, setUploadErrorMessage, extraClass } =
        props;
    const refLabLogo = useRef<HTMLDivElement>(null);
    const { makeDroppableCapable, getImagePath, makeDraggableCapableForImageResize } = useFileManagement();
    const [logoImg, setLogo] = useState<string | undefined>(undefined);
    const [logoCustomizationStyle, _setLogoCustomizationStyle] = useState<React.CSSProperties>({ backgroundSize: "cover" });
    const { updateLogoCustomization } = useLabs();
    const [isLogoCustomizing, setIsLogoCustomizing] = useState<boolean>(false);
    const [originalLogoCustomizingData, setOriginalLogoCustomizingData] = useState<TLogoCustomization | null>(null);
    const { userInfo: currentUserInfo, setUserInfo, updateUserInfoInStorage } = useAuthContext();

    const configureLogoClickHandler = () => {
        setIsLogoCustomizing(true);
        makeDraggableCapableForImageResize(refLabLogo, customizeLogoMouseMoveHandler);
        setOriginalLogoCustomizingData(userInfo.other?.logoCustomization ? { ...userInfo.other.logoCustomization } : null);
        props.onCustomizeModeChange?.(true);
    };

    const cancelLogoCustomization = () => {
        setIsLogoCustomizing(false);
        setLogoCustomizationStyle(originalLogoCustomizingData || undefined);
        if (originalLogoCustomizingData) {
            userInfo.other.logoCustomization = originalLogoCustomizingData;
        }
        setOriginalLogoCustomizingData(null);
        setDroppableCapableLogo();
        props.onCustomizeModeChange?.(false);
    };

    const setLogoCustomizationStyle = (customization: TLogoCustomization | undefined) => {
        if (!customization) {
            _setLogoCustomizationStyle({ backgroundSize: "cover" });
            return;
        }

        _setLogoCustomizationStyle({
            backgroundSize: customization.percentageSize + "%",
            backgroundPositionX: customization.left + "%",
            backgroundPositionY: customization.top + "%",
            backgroundRepeat: "no-repeat",
        });
    };

    const saveLogoCustomization = async () => {
        await updateLogoCustomization(userInfo.other.logoCustomization || null);
        if (currentUserInfo?.type === "lab" && currentUserInfo.other) {
            const updatedUserInfo = {
                ...currentUserInfo,
                other: {
                    ...currentUserInfo.other,
                    logoCustomization: userInfo.other.logoCustomization,
                },
            };
            updateUserInfoInStorage(updatedUserInfo);
            setUserInfo(updatedUserInfo);
        }
        window.wwgLabInfo_dataWasModified = true;
        setIsLogoCustomizing(false);
        setDroppableCapableLogo();
        props.onCustomizeModeChange?.(false);
    };

    const updateLogo = (newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (errorMessage) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage?.(errorMessage);
            return;
        }

        if (inappropriateContent) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage?.("");
            setInappropriateContent(true);
            setLogo(ASSETS_ROUTES.IMGS.labAvatarUrl);
        } else if (unsupportedImageType) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage?.("");
            setUnsupportedImageType(true);
            setLogo(ASSETS_ROUTES.IMGS.labAvatarUrl);
        } else if (!newfile) {
            setLogo(ASSETS_ROUTES.IMGS.labAvatarUrl);
        } else {
            const cacheKill = `?${getCacheKillQueryString()}`;
            setUploadErrorMessage?.("");
            setInappropriateContent(false);
            setUnsupportedImageType(false);
            setLogo(getImagePath(newfile, "fixed", !editable ? userInfo.guid : undefined) + cacheKill);
        }
    };

    const customizeLogoMouseMoveHandler = (x: number, y: number) => {
        const values: TLogoCustomization = userInfo.other?.logoCustomization || {
            left: 0,
            percentageSize: 100,
            top: 0,
        };
        values.left -= 0.2 * x;
        values.top -= 0.2 * y;
        userInfo.other.logoCustomization = values;
        setLogoCustomizationStyle(values);
    };

    const zoomAndSaveClickHandler = (command: "zoomin" | "zoomout" | "reset") => {
        const values: TLogoCustomization = userInfo.other?.logoCustomization || {
            left: 0,
            percentageSize: 100,
            top: 0,
        };

        switch (command) {
            case "zoomin":
                values.percentageSize += 5;
                break;
            case "zoomout":
                values.percentageSize -= 5;
                break;
            case "reset":
                values.left = 0;
                values.percentageSize = 100;
                values.top = 0;
                break;
            default:
                break;
        }

        userInfo.other.logoCustomization = values;
        setLogoCustomizationStyle(values);
    };

    const setDroppableCapableLogo = () => {
        makeDroppableCapable(refLabLogo, "logo", "image", "fixed", (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
            updateLogo(newfile, inappropriateContent, unsupportedImageType, errorMessage);
            props.onboardingStepCompleted?.();
        });
    };

    useEffect(() => {
        if (editable) {
            setDroppableCapableLogo();
        }
        updateLogo(userInfo?.images?.logo);
        if (userInfo.other) {
            setLogoCustomizationStyle(userInfo.other.logoCustomization);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    const componentLogoSection = (
        <>
            <div
                ref={refLabLogo}
                className={
                    "rounded-circle border border-2 d-inline-block position-relative " +
                    extraClass +
                    " " +
                    (isLogoCustomizing ? "profile-logo-cursor-grabbing " : "") +
                    (workingState === "done" ? "" : "cssLoading")
                }
                style={
                    workingState === "done"
                        ? {
                              backgroundImage: `url(${logoImg || ASSETS_ROUTES.IMGS.labAvatarUrl})`,
                              ...logoCustomizationStyle,
                          }
                        : logoCustomizationStyle
                }
            >
                {editable && !isSomePartBeingEdited && !isLogoCustomizing && (
                    <FileUploaderComponent
                        relatedComponent={refLabLogo}
                        tooltipPosition="left"
                        fileToolTipText="Click here to edit your logo, or drag and drop it onto this area. Images should be in .JPG, .JPEG, .PNG, .HEIC or .HEIF formats. Your profile image should be minimum 500x500 pixels. "
                    />
                )}
                {editable && !isSomePartBeingEdited && !isLogoCustomizing && (
                    <button
                        type="button"
                        className="btn bg-white position-absolute fileTooltip left shadow-sm"
                        style={{ right: "-1rem" }}
                        data-tooltip="Click here to customize your logo"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            configureLogoClickHandler();
                        }}
                    >
                        <i className="fa-solid fa-sliders text-center shadow editIcon"></i>
                    </button>
                )}
            </div>
            {editable && (
                <div className="mt-4">
                    {editable && !isSomePartBeingEdited && isLogoCustomizing && (
                        <>
                            <div className="text-muted fw-semibold small my-3">
                                <i className="fa-solid fa-hand-pointer me-2"></i>
                                <span>Drag to reposition your logo</span>
                            </div>
                            <div className="btn-group mb-3 gap-3 " aria-label="Logo zoom controls">
                                <button className="btn btn-light px-4 py-3 rounded-4 shadow-sm" type="button" onClick={() => zoomAndSaveClickHandler("zoomin")}>
                                    <i className="fa-solid fa-magnifying-glass-plus"></i>
                                </button>
                                <button
                                    className="btn btn-light px-4 py-3 rounded-4 shadow-sm"
                                    type="button"
                                    onClick={() => zoomAndSaveClickHandler("zoomout")}
                                >
                                    <i className="fa-solid fa-magnifying-glass-minus"></i>
                                </button>
                                <button className="btn btn-light px-4 py-3 rounded-4 shadow-sm" type="button" onClick={() => zoomAndSaveClickHandler("reset")}>
                                    <i className="fa-solid fa-rotate-left"></i>
                                </button>
                            </div>
                            <br />
                            <div className="d-inline justify-content-center align-items-center gap-2 mt-3 pt-2">
                                <button type="button" className="btn btn-primary rounded-3 px-3 shadow-sm" onClick={saveLogoCustomization}>
                                    <i className="fa-solid fa-floppy-disk me-2"></i> Save
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-link rounded-3 text-danger text-decoration-none px-2"
                                    onClick={cancelLogoCustomization}
                                >
                                    <i className="fa-regular fa-trash-can me-2"></i> Cancel
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );

    return componentLogoSection;
};
